import { useState, useRef, useEffect } from "react";
import { Share2, Check, Copy, X } from "lucide-react";
import {
    shareToX, shareToLinkedIn, shareToFacebook,
    copyLink, tryNativeShare, isMobileShareCapable,
} from "@/lib/share";

/**
 * Genbrugelig share-knap. Native share-sheet på mobil; dropdown-menu på desktop.
 *
 * Props: url, title, hashtags[], via, testIdPrefix, label
 */
export default function ShareButton({
    url,
    title,
    hashtags = [],
    via = "",
    testIdPrefix = "share",
    label = "Del",
}) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const ref = useRef(null);
    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const shareTitle = title || "Løftebarometeret";

    // Luk menu ved klik udenfor
    useEffect(() => {
        if (!open) return undefined;
        function onClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    async function onTrigger() {
        if (isMobileShareCapable()) {
            const ok = await tryNativeShare({ url: shareUrl, title: shareTitle });
            if (ok) {
                setOpen(false);
                return;
            }
        }
        setOpen((v) => !v);
    }

    const close = () => setOpen(false);

    function handleX() {
        shareToX({ url: shareUrl, title: shareTitle, hashtags, via });
        close();
    }
    function handleLinkedIn() {
        shareToLinkedIn({ url: shareUrl });
        close();
    }
    function handleFacebook() {
        shareToFacebook({ url: shareUrl });
        close();
    }
    async function handleCopy() {
        await copyLink(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    }

    return (
        <div ref={ref} className="relative inline-block" data-testid={`${testIdPrefix}-root`}>
            <button
                type="button"
                onClick={onTrigger}
                data-testid={`${testIdPrefix}-trigger`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-sm transition-colors"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Share2 className="w-4 h-4" />
                {label}
            </button>

            {open && (
                <div
                    role="menu"
                    data-testid={`${testIdPrefix}-menu`}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#E5E8F0] z-30 overflow-hidden"
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#EEF1F8]">
                        <span className="text-xs font-semibold text-[#0C2A6B] uppercase tracking-wide">Del på</span>
                        <button
                            onClick={close}
                            className="p-1 rounded-full hover:bg-[#EEF1F8] text-[#4F5A7A]"
                            aria-label="Luk"
                            data-testid={`${testIdPrefix}-close`}
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <ShareMenuItem
                        testId={`${testIdPrefix}-x`}
                        onClick={handleX}
                        bg="#000000"
                        label="X (tidl. Twitter)"
                        icon={<XLogo />}
                    />
                    <ShareMenuItem
                        testId={`${testIdPrefix}-linkedin`}
                        onClick={handleLinkedIn}
                        bg="#0A66C2"
                        label="LinkedIn"
                        icon={<LinkedInLogo />}
                    />
                    <ShareMenuItem
                        testId={`${testIdPrefix}-facebook`}
                        onClick={handleFacebook}
                        bg="#1877F2"
                        label="Facebook"
                        icon={<FacebookLogo />}
                    />
                    <div className="border-t border-[#EEF1F8]" />
                    <ShareMenuItem
                        testId={`${testIdPrefix}-copy`}
                        onClick={handleCopy}
                        bg="#0C2A6B"
                        label={copied ? "Kopieret!" : "Kopiér link"}
                        icon={
                            copied
                                ? <Check className="w-4 h-4 text-white" />
                                : <Copy className="w-4 h-4 text-white" />
                        }
                    />
                </div>
            )}
        </div>
    );
}

function ShareMenuItem({ onClick, bg, label, icon, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F8F9FC] transition-colors text-left"
            role="menuitem"
        >
            <span
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: bg }}
            >
                {icon}
            </span>
            <span className="text-sm font-semibold text-[#0C2A6B]">{label}</span>
        </button>
    );
}

function XLogo() {
    return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}
function LinkedInLogo() {
    return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white">
            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
        </svg>
    );
}
function FacebookLogo() {
    return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white">
            <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
        </svg>
    );
}
